/**
 * MIS PARTES — F12.1 (técnico, en línea) · F14.3 (con y sin conexión)
 *
 * «¿Qué tengo que hacer ahora?» y, dentro de cada parte, el trabajo de campo: activo, centro y ubicación,
 * instrucciones, documentos, material, stock de la furgoneta y la intervención (iniciar, diagnóstico, trabajo,
 * observaciones, fotos, consumos y finalizar).
 *
 * UNA pantalla, dos fuentes (mismo criterio que «Mis trabajos» de Limpieza, F14.2):
 *   · con conexión, el servidor (`/api/mantenimiento/mis-partes`, `/api/mantenimiento/campo/partes/:id`), con lo
 *     guardado en el dispositivo y aún sin llegar proyectado encima;
 *   · sin conexión (o con el servidor caído), el paquete del dispositivo, tras el PIN local.
 * Las operaciones van siempre por `offline/ejecucion-mantenimiento.ts`, que decide el camino; la pantalla sólo cuenta
 * dónde quedó cada cosa: «Guardado en el servidor» o «Guardado en este dispositivo · pendiente de sincronizar».
 *
 * Móvil primero (390 px): tarjetas y listas, nunca tablas de escritorio encogidas; botones de 44 px.
 *
 * Lo que no se hace aquí (con conexión, en la ficha completa del parte): cerrar el parte, consumir artículos con
 * número de serie, devoluciones y la gestión (planificar, asignar, anular).
 */
import { type ReactNode, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { api, type AlmacenCampo, type ParteCampo, type ParteCampoEnLinea, type ParteResumen, type TipoNotaCampo, type Usuario } from '../api.ts';
import { Cargando, Error as ErrorCaja, fechaHora, useDatos, Vacio } from '../componentes/base.tsx';
import { abrirArchivoRuta, EstadoPtEtiqueta, MOTIVOS_PENDIENTE, PrioridadEtiqueta, programacion, RESULTADOS, TIPOS_PT } from '../componentes/mantenimiento.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { antiguedadDatos, nivelCaducidad } from '../offline/caducidad.ts';
import { useSesionOfflineActiva } from '../offline/contextoPerfil.tsx';
import { listarDocumentosLocalesDe, type DocumentoOfflineLocal, type MetaOffline, type Mutacion, type PayloadMntFinalizar } from '../offline/db.ts';
import { ejecutarOperacionMnt, subirFotoParte, type ContextoMnt, type ResultadoAccionMnt } from '../offline/ejecucion-mantenimiento.ts';
import { GUARDADO_EN_DISPOSITIVO, GUARDADO_EN_SERVIDOR } from '../offline/estados-sync.ts';
import {
  abiertaEnOtroParte,
  cantidad,
  estimarStock,
  leerCampoLocal,
  opcionesFin,
  prepararConsumo,
  prepararFin,
  prepararFoto,
  prepararInicio,
  prepararNota,
  proyectarParte,
  textoFotografiaStock,
  type EntornoParte,
  type EstimacionStock,
  type MetaCaducidad,
  type VistaParteCampo,
} from '../offline/mantenimiento-local.ts';
import { useConectividad } from '../pwa.ts';
import { BotonAbrirDocumentoLocal } from './offline.tsx';
import { PuertaOfflineSegura } from './offline-perfil.tsx';
import { AvisoCaducidad, useActualizarDatosDispositivo, useCambiosCola, useMeta, usePerfilDesbloqueadoDe } from './mis-trabajos.tsx';
import { TarjetaParte } from './partes.tsx';

const TACTIL = { minHeight: 44 } as const;
const LISTA = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 } as const;

export function esTecnico(roles: readonly { rol: string }[]): boolean {
  return roles.some((r) => r.rol === 'TECNICO_MANTENIMIENTO');
}

// ------------------------------------------------------------------ acceso sin sesión

/**
 * `/mantenimiento/mis-partes*` abierto SIN sesión de servidor (recarga o arranque sin conexión): sólo el PIN local, y
 * sólo para un perfil de TÉCNICO. El PIN no autentica contra el servidor: sólo descifra lo que ya está aquí.
 */
export function MisPartesSinSesion({ detalle }: { detalle: boolean }): ReactNode {
  return (
    <PuertaOfflineSegura>
      <SoloTecnicoLocal>{detalle ? <ParteEnDispositivo /> : <MisPartesEnDispositivo />}</SoloTecnicoLocal>
    </PuertaOfflineSegura>
  );
}

function SoloTecnicoLocal({ children }: { children: ReactNode }): ReactNode {
  const { roles } = useSesionOfflineActiva();
  return esTecnico(roles) ? <>{children}</> : <Navigate to="/offline" replace />;
}

// ------------------------------------------------------------------ lista

/** F12.1 — «¿Qué tengo que hacer ahora?»: los tramos que da el servidor, en este orden, sin grupos vacíos. */
const GRUPOS_MIS_PARTES: readonly [string, string, string][] = [
  ['AHORA', 'En curso', 'Lo que tienes empezado.'],
  ['HOY', 'Para hoy', 'Programados para hoy (o que se han quedado atrás).'],
  ['PENDIENTE', 'Para retomar', 'Pendientes o reabiertos: vuelve cuando se resuelva el motivo.'],
  ['PROXIMO', 'Próximos', ''],
  ['SIN_FECHA', 'Sin fecha', 'Aún sin programar.'],
  ['FINALIZADO', 'Finalizados: falta cerrarlos', 'El trabajo está hecho; ciérralos cuando el material esté resuelto.'],
];

export function PaginaMisPartes(): ReactNode {
  const conectividad = useConectividad();
  const { usuario } = useSesion();
  if (conectividad === 'ONLINE' && usuario !== null) return <MisPartesEnLinea usuario={usuario} />;
  return <PuertaOfflineSegura><SoloTecnicoLocal><MisPartesEnDispositivo /></SoloTecnicoLocal></PuertaOfflineSegura>;
}

function MisPartesEnLinea({ usuario }: { usuario: Usuario }): ReactNode {
  const partes = useDatos(() => api.get<{ filas: ParteResumen[] }>('/api/mantenimiento/mis-partes'), []);
  const perfilId = usePerfilDesbloqueadoDe(usuario);
  const { meta, releer } = useMeta(perfilId);
  const dispositivo = useActualizarDatosDispositivo(perfilId, meta, releer);
  return (
    <Shell migas={[{ texto: 'Mantenimiento' }, { texto: 'Mis partes' }]} titulo="Mis partes">
      {partes.cargando && partes.datos === null && <Cargando texto="Cargando tus partes…" />}
      {partes.error !== null && <ErrorCaja error={partes.error} reintentar={partes.recargar} />}
      {partes.datos !== null && <GruposPartes filas={partes.datos.filas} />}
      {esTecnico(usuario.roles) && <PanelDispositivo perfilId={perfilId} meta={meta} {...dispositivo} />}
    </Shell>
  );
}

function MisPartesEnDispositivo(): ReactNode {
  const { perfilId } = useSesionOfflineActiva();
  const [filas, setFilas] = useState<ParteResumen[] | null>(null);
  const [error, setError] = useState(false);
  const { meta, releer } = useMeta(perfilId);
  const cargar = useCallback(() => {
    void leerCampoLocal(perfilId).then((d) => {
      if (d === null) { setFilas([]); return; }
      //  El estado de cada parte, con lo hecho en este dispositivo encima (p. ej. «En curso» tras iniciar sin red).
      setFilas(d.mantenimiento.partes.map((p) => {
        const v = proyectarParte(p, d.operaciones.filter((m) => m.idOt === p.parte.id));
        const enCurso = v.abierta !== null;
        return { ...p.parte, estado: v.estado as ParteResumen['estado'], franja: enCurso ? 'AHORA' : p.parte.franja };
      }));
      setError(false);
    }).catch(() => setError(true));
    releer();
  }, [perfilId, releer]);
  useEffect(cargar, [cargar]);
  useCambiosCola(cargar);
  return (
    <Shell migas={[{ texto: 'Mantenimiento' }, { texto: 'Mis partes' }]} titulo="Mis partes">
      <p className="nota info" role="status">
        Sin conexión con el servidor: trabajas con los datos guardados en este dispositivo{meta !== null ? ` (${antiguedadDatos(meta.generadoEn)})` : ''}. Lo que hagas se enviará solo al volver la conexión.
      </p>
      {meta !== null && <AvisoCaducidad nivel={nivelCaducidad(meta)} />}
      {error && <Vacio titulo="No se han podido leer los datos de este dispositivo. Vuelve a prepararlos cuando tengas conexión." />}
      {!error && filas === null && <Cargando texto="Cargando tus partes…" />}
      {filas !== null && <GruposPartes filas={filas} />}
    </Shell>
  );
}

function GruposPartes({ filas }: { filas: readonly ParteResumen[] }): ReactNode {
  if (filas.length === 0) {
    return <div className="panel"><Vacio titulo="No tienes partes asignados">Cuando te asignen un parte de trabajo aparecerá aquí.</Vacio></div>;
  }
  return (
    <>
      {GRUPOS_MIS_PARTES.map(([clave, titulo, ayuda]) => {
        const deGrupo = filas.filter((p) => (p.franja ?? 'SIN_FECHA') === clave);
        if (deGrupo.length === 0) return null;
        return (
          <section key={clave} className="grupo-mis-partes" aria-labelledby={`mp-${clave}`}>
            <h2 id={`mp-${clave}`} className="titulo-grupo">{titulo} <span className="contador">{deGrupo.length}</span></h2>
            {ayuda !== '' && <p className="pequena tenue" style={{ marginTop: 0 }}>{ayuda}</p>}
            <ul className="lista-mis-partes" aria-label={titulo}>
              {deGrupo.map((p) => <li key={p.id}><TarjetaParte parte={p} a={`/mantenimiento/mis-partes/${p.id}`} /></li>)}
            </ul>
          </section>
        );
      })}
    </>
  );
}

/** Con conexión: preparar el dispositivo, o la antigüedad de sus datos y «Actualizar datos». */
function PanelDispositivo({ perfilId, meta, actualizando, actualizar, error }: {
  perfilId: string | null; meta: MetaOffline | null; actualizando: boolean; actualizar: () => void; error: string | null;
}): ReactNode {
  return (
    <div className="panel" aria-label="Trabajo sin conexión">
      <div className="panel-cuerpo fila" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {perfilId === null || meta === null ? (
          <Link to="/offline" className="boton" style={{ ...TACTIL, display: 'inline-flex', alignItems: 'center' }}>Preparar sin conexión</Link>
        ) : (
          <>
            <span className="suave pequena">Datos en este dispositivo: {antiguedadDatos(meta.generadoEn)}</span>
            <button type="button" style={TACTIL} disabled={actualizando} onClick={actualizar}>{actualizando ? 'Actualizando…' : 'Actualizar datos'}</button>
          </>
        )}
        {error !== null && <span className="nota error pequena" role="alert">{error}</span>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ un parte

export function PaginaParteCampo(): ReactNode {
  const conectividad = useConectividad();
  const { usuario } = useSesion();
  if (conectividad === 'ONLINE' && usuario !== null) return <ParteEnLinea usuario={usuario} />;
  return <PuertaOfflineSegura><SoloTecnicoLocal><ParteEnDispositivo /></SoloTecnicoLocal></PuertaOfflineSegura>;
}

/** Todo lo que la ficha necesita para pintar y para actuar. */
interface Entorno {
  readonly vista: VistaParteCampo;
  readonly stock: EstimacionStock[];
  readonly fotografiadoEn: string | null;
  readonly truncado: boolean;
  readonly almacenes: readonly AlmacenCampo[];
  readonly ctx: ContextoMnt;
  readonly entorno: EntornoParte;
  readonly meta: MetaCaducidad;
  readonly abrirDocumento: (d: { id: string; titulo: string; idEvidencia: string }) => ReactNode;
  readonly documentos: readonly { id: string; titulo: string; idEvidencia: string; origen: string }[];
  readonly alCambiar: () => void;
}

function ParteEnLinea({ usuario }: { usuario: Usuario }): ReactNode {
  const { id = '' } = useParams();
  const perfilId = usePerfilDesbloqueadoDe(usuario);
  const datos = useDatos(() => api.get<ParteCampoEnLinea>(`/api/mantenimiento/campo/partes/${encodeURIComponent(id)}`), [id]);
  const [local, setLocal] = useState<{ operaciones: readonly Mutacion[]; otra: string | null }>({ operaciones: [], otra: null });
  const releerLocal = useCallback(() => {
    if (perfilId === null) { setLocal({ operaciones: [], otra: null }); return; }
    void leerCampoLocal(perfilId).then((d) => {
      if (d === null) { setLocal({ operaciones: [], otra: null }); return; }
      setLocal({ operaciones: d.operaciones, otra: abiertaEnOtroParte(d.mantenimiento.partes, d.operaciones, id) });
    }).catch(() => undefined);
  }, [perfilId, id]);
  useEffect(releerLocal, [releerLocal]);
  const { recargar } = datos;
  const alCambiar = useCallback(() => { recargar(); releerLocal(); }, [recargar, releerLocal]);
  useCambiosCola(alCambiar);
  const migas = [{ texto: 'Mis partes', a: '/mantenimiento/mis-partes' }, { texto: datos.datos?.parte.parte.codigo ?? '…' }];
  if (datos.cargando && datos.datos === null) return <Shell titulo="Parte" migas={migas}><Cargando /></Shell>;
  if (datos.error !== null || datos.datos === null) return <Shell titulo="Parte" migas={migas}><ErrorCaja error={datos.error} reintentar={datos.recargar} /></Shell>;
  const d = datos.datos;
  const vista = proyectarParte(d.parte, local.operaciones.filter((m) => m.idOt === d.parte.parte.id), d.stock);
  const e: Entorno = {
    vista,
    stock: estimarStock(d.stock, local.operaciones),
    fotografiadoEn: d.stock.fotografiadoEn,
    truncado: d.stock.truncado,
    almacenes: d.stock.almacenes,
    ctx: { perfilId, enLinea: true },
    entorno: { vista, fuente: 'SERVIDOR', abiertaEnOtroParte: local.otra },
    meta: null,
    documentos: d.documentos.map((x) => ({ id: x.id, titulo: x.titulo, idEvidencia: x.id, origen: x.origen })),
    abrirDocumento: (doc) => <BotonAbrirEnLinea idEvidencia={doc.idEvidencia} />,
    alCambiar,
  };
  return <VistaParte e={e} migas={migas} aviso={null} />;
}

function ParteEnDispositivo(): ReactNode {
  const { id = '' } = useParams();
  const { perfilId } = useSesionOfflineActiva();
  const { meta, releer } = useMeta(perfilId);
  const [datos, setDatos] = useState<{ campo: ParteCampo & { fueraDeVentana?: boolean }; resto: Awaited<ReturnType<typeof leerCampoLocal>>; documentos: DocumentoOfflineLocal[] } | null | undefined>(undefined);
  const cargar = useCallback(() => {
    void (async () => {
      const d = await leerCampoLocal(perfilId);
      const campo = d?.mantenimiento.partes.find((p) => p.parte.id === id) ?? null;
      if (d === null || campo === null) { setDatos(null); return; }
      setDatos({ campo, resto: d, documentos: await listarDocumentosLocalesDe(perfilId, id) });
    })().catch(() => setDatos(null));
    releer();
  }, [perfilId, id, releer]);
  useEffect(cargar, [cargar]);
  useCambiosCola(cargar);
  const migas = [{ texto: 'Mis partes', a: '/mantenimiento/mis-partes' }, { texto: datos?.campo.parte.codigo ?? '…' }];
  if (datos === undefined) return <Shell titulo="Parte" migas={migas}><Cargando /></Shell>;
  if (datos === null || datos.resto === null) {
    return <Shell titulo="Parte" migas={migas}><Vacio titulo="Este parte no está en los datos de este dispositivo." /></Shell>;
  }
  const { mantenimiento, operaciones } = datos.resto;
  const vista = proyectarParte(datos.campo, operaciones.filter((m) => m.idOt === id), mantenimiento.stock);
  const porId = new Map(datos.documentos.map((x) => [x.idEvidenciaMnt ?? x.id, x] as const));
  const e: Entorno = {
    vista,
    stock: estimarStock(mantenimiento.stock, operaciones),
    fotografiadoEn: mantenimiento.stock.fotografiadoEn,
    truncado: mantenimiento.stock.truncado,
    almacenes: mantenimiento.stock.almacenes,
    ctx: { perfilId, enLinea: false },
    entorno: { vista, fuente: 'DISPOSITIVO', abiertaEnOtroParte: abiertaEnOtroParte(mantenimiento.partes, operaciones, id) },
    meta,
    documentos: datos.documentos.map((x) => ({ id: x.id, titulo: x.titulo, idEvidencia: x.idEvidenciaMnt ?? x.id, origen: x.tipoDocumento })),
    abrirDocumento: (doc) => {
      const local = porId.get(doc.idEvidencia);
      if (local === undefined) return null;
      return local.disponibleOffline
        ? <BotonAbrirDocumentoLocal idPerfil={perfilId} doc={local} />
        : <span className="suave pequena">No descargado: ábrelo con conexión.</span>;
    },
    alCambiar: cargar,
  };
  return <VistaParte e={e} migas={migas} aviso={meta === null ? null : <AvisoCaducidad nivel={nivelCaducidad(meta)} />} />;
}

function BotonAbrirEnLinea({ idEvidencia }: { idEvidencia: string }): ReactNode {
  const [abriendo, setAbriendo] = useState(false);
  return (
    <button type="button" className="pequeno" style={TACTIL} disabled={abriendo}
      onClick={() => { setAbriendo(true); void abrirArchivoRuta(`/api/mantenimiento/evidencias/${idEvidencia}/archivo`).finally(() => setAbriendo(false)); }}>
      {abriendo ? 'Abriendo…' : 'Abrir'}
    </button>
  );
}

type Mensaje = { readonly tono: 'exito' | 'info' | 'error'; readonly texto: string };

function mensajeDe(r: ResultadoAccionMnt, hecho: string): Mensaje {
  if (!r.ok) return { tono: 'error', texto: r.motivo };
  const donde = r.donde === 'SERVIDOR' ? GUARDADO_EN_SERVIDOR : GUARDADO_EN_DISPOSITIVO;
  return { tono: r.donde === 'SERVIDOR' ? 'exito' : 'info', texto: `${hecho}. ${donde}.${r.aviso === null ? '' : ` ${r.aviso}`}` };
}

const hora = (iso: string): string => new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

function VistaParte({ e, migas, aviso }: { e: Entorno; migas: { texto: string; a?: string }[]; aviso: ReactNode }): ReactNode {
  const { vista } = e;
  const p = vista.campo.parte;
  const [mensaje, setMensaje] = useState<Mensaje | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [abierto, setAbierto] = useState<'NOTA' | 'FOTO' | 'CONSUMO' | 'FIN' | null>(null);
  const inicio = prepararInicio(e.entorno, e.meta);

  async function hacer(f: () => Promise<ResultadoAccionMnt>, hecho: string): Promise<boolean> {
    setEnviando(true);
    setMensaje(null);
    const r = await f().catch((x: unknown) => ({ ok: false as const, motivo: x instanceof Error ? x.message : 'No se ha podido guardar.' }));
    setEnviando(false);
    setMensaje(mensajeDe(r, hecho));
    if (r.ok) { setAbierto(null); e.alCambiar(); }
    return r.ok;
  }

  function iniciar(): void {
    const prep = prepararInicio(e.entorno, e.meta);
    if (!prep.ok) { setMensaje({ tono: 'error', texto: prep.motivo }); return; }
    void hacer(() => ejecutarOperacionMnt(e.ctx, prep.op, prep.aviso), 'Intervención iniciada');
  }

  return (
    <Shell titulo={p.titulo} migas={migas}>
      {!e.ctx.enLinea && <p className="nota info" role="status">Sin conexión con el servidor: lo que hagas se guarda en este dispositivo y se enviará solo al volver la conexión.</p>}
      {aviso}
      {vista.fueraDeVentana && (
        <p className="nota aviso" role="status">Este parte ya no venía en los últimos datos del servidor (puede que se haya reasignado, anulado o cerrado). Lo que tenías guardado sigue aquí hasta que se revise.</p>
      )}

      <div className="panel" aria-label="Resumen del parte">
        <div className="panel-cuerpo">
          <div className="fila" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 17 }}>{p.fechaProgramada === null ? 'Sin fecha' : programacion(p.fechaProgramada, p.horaProgramada)}</strong>
            <EstadoPtEtiqueta estado={vista.estado} />
          </div>
          <div>{[p.centro.nombre, p.ubicacion].filter((x) => x != null && x !== '').join(' · ')}</div>
          {p.direccionCentro !== null && <div className="suave pequena">{p.direccionCentro}</div>}
          <div className="fila" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            <PrioridadEtiqueta prioridad={p.prioridad} />
            <span className="etiqueta neutra">{TIPOS_PT[p.tipo]}</span>
            {p.motivoPendiente !== null && <span className="etiqueta aviso">Pendiente: {MOTIVOS_PENDIENTE[p.motivoPendiente]}</span>}
          </div>
          <div className="pequena tenue" style={{ marginTop: 4 }}>{p.codigo}</div>
          {vista.pendientes > 0 && (
            <p className="nota info pequena" role="status" style={{ margin: '8px 0 0' }}>
              {vista.pendientes === 1 ? '1 cambio guardado en este dispositivo' : `${String(vista.pendientes)} cambios guardados en este dispositivo`} · pendiente de sincronizar.
            </p>
          )}
          {vista.enRevision > 0 && (
            <p className="nota aviso pequena" role="status" style={{ margin: '8px 0 0' }}>
              {vista.enRevision === 1 ? '1 cambio necesita revisión' : `${String(vista.enRevision)} cambios necesitan revisión`}. <Link to="/offline/revision">Revisar</Link>
            </p>
          )}
        </div>
      </div>

      <SeccionInstrucciones campo={vista.campo} />
      <SeccionActivos campo={vista.campo} />
      <SeccionDocumentos e={e} />

      <section className="panel" aria-label="Intervención">
        <div className="panel-cabecera"><h2>Intervención</h2></div>
        <div className="panel-cuerpo">
          {mensaje !== null && <p className={`nota ${mensaje.tono}`} role={mensaje.tono === 'error' ? 'alert' : 'status'}>{mensaje.texto}</p>}
          {vista.abierta === null ? (
            inicio.ok ? (
              <button type="button" className="principal grande" style={TACTIL} disabled={enviando} onClick={iniciar}>
                {enviando ? 'Guardando…' : p.intervenciones.length > 0 ? 'Retomar trabajo' : 'Iniciar intervención'}
              </button>
            ) : (
              <p className="suave" style={{ margin: 0 }}>{inicio.motivo}</p>
            )
          ) : (
            <>
              <p style={{ margin: '0 0 8px' }}>
                <strong>En curso desde las {hora(vista.abierta.inicioEn)}</strong>{' '}
                <span className={`etiqueta ${vista.abierta.guardado.clase}`}>{vista.abierta.guardado.texto}</span>
              </p>
              <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
                {([['NOTA', 'Añadir nota'], ['FOTO', 'Añadir foto'], ['CONSUMO', 'Registrar material'], ['FIN', 'Finalizar intervención']] as const).map(([k, t]) => (
                  <button key={k} type="button" className={k === 'FIN' ? 'principal' : undefined} style={TACTIL} aria-expanded={abierto === k}
                    onClick={() => { setMensaje(null); setAbierto((x) => (x === k ? null : k)); }}>{t}</button>
                ))}
              </div>
              {abierto === 'NOTA' && <FormNota e={e} enviando={enviando} hacer={hacer} />}
              {abierto === 'FOTO' && <FormFoto e={e} enviando={enviando} hacer={hacer} />}
              {abierto === 'CONSUMO' && <FormConsumo e={e} enviando={enviando} hacer={hacer} />}
              {abierto === 'FIN' && <FormFin e={e} enviando={enviando} hacer={hacer} />}
            </>
          )}
        </div>
      </section>

      <SeccionHistorial vista={vista} />
      <SeccionMaterial e={e} />

      {e.ctx.enLinea && (
        <p className="pequena" style={{ margin: '4px 0 16px' }}>
          Para cerrar el parte, ver su historial completo o consumir artículos con número de serie: <Link to={`/mantenimiento/partes/${p.id}`}>ficha completa del parte</Link>.
        </p>
      )}
    </Shell>
  );
}

function SeccionInstrucciones({ campo }: { campo: ParteCampo }): ReactNode {
  const p = campo.parte;
  if (p.descripcion === null && campo.plan === null && p.detallePendiente === null) return null;
  return (
    <section className="panel" aria-label="Qué hay que hacer">
      <div className="panel-cabecera"><h2>Qué hay que hacer</h2></div>
      <div className="panel-cuerpo">
        {p.descripcion !== null && <p style={{ marginTop: 0, whiteSpace: 'pre-wrap' }}>{p.descripcion}</p>}
        {campo.plan !== null && (
          <div className="pequena">
            <span className="suave">Preventivo:</span> {campo.plan.nombre} ({campo.plan.codigo}) · {campo.plan.tipoTrabajo}
            {campo.plan.descripcion !== null && <p style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{campo.plan.descripcion}</p>}
          </div>
        )}
        {p.detallePendiente !== null && <p className="pequena" style={{ marginBottom: 0 }}><span className="suave">Por qué quedó pendiente:</span> {p.detallePendiente}</p>}
      </div>
    </section>
  );
}

function SeccionActivos({ campo }: { campo: ParteCampo }): ReactNode {
  if (campo.activos.length === 0) return null;
  return (
    <section className="panel" aria-label="Activos">
      <div className="panel-cabecera"><h2>{campo.activos.length === 1 ? 'Activo' : 'Activos'}</h2></div>
      <div className="panel-cuerpo">
        <ul style={LISTA}>
          {campo.activos.map((a) => (
            <li key={a.id} className="tarjeta-linea">
              <div className="fila" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <strong>{a.nombre}</strong>
                {a.estado === 'BAJA' && <span className="etiqueta error">De baja</span>}
              </div>
              <div className="pequena">{a.codigo} · {a.tipo}</div>
              {a.ubicacion !== null && <div className="pequena suave">{a.ubicacion}</div>}
              {[a.fabricante, a.modelo].some((x) => x !== null) && <div className="pequena">{[a.fabricante, a.modelo].filter((x) => x !== null).join(' · ')}</div>}
              {a.numeroSerie !== null && <div className="pequena"><span className="suave">Nº de serie:</span> {a.numeroSerie}</div>}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function SeccionDocumentos({ e }: { e: Entorno }): ReactNode {
  if (e.documentos.length === 0) return null;
  return (
    <section className="panel" aria-label="Documentos">
      <div className="panel-cabecera"><h2>Documentos</h2></div>
      <div className="panel-cuerpo">
        <ul style={LISTA}>
          {e.documentos.map((d) => (
            <li key={d.id} className="fila" style={{ gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <span>{d.titulo} <span className="suave pequena">{d.origen === 'ACTIVO' ? '(del activo)' : '(del parte)'}</span></span>
              {e.abrirDocumento(d)}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const TIPO_NOTA: Readonly<Record<TipoNotaCampo, string>> = { DIAGNOSTICO: 'Diagnóstico', TRABAJO: 'Trabajo', OBSERVACION: 'Observación' };

function SeccionHistorial({ vista }: { vista: VistaParteCampo }): ReactNode {
  if (vista.intervenciones.length === 0 && vista.notas.length === 0 && vista.fotos.length === 0) return null;
  return (
    <section className="panel" aria-label="Historial de la intervención">
      <div className="panel-cabecera"><h2>Historial</h2></div>
      <div className="panel-cuerpo">
        <ul style={LISTA} aria-label="Intervenciones">
          {vista.intervenciones.map((i) => (
            <li key={i.clave} className="tarjeta-linea">
              <div className="fila" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <strong>{fechaHora(i.inicioEn)}{i.finEn !== null ? ` – ${hora(i.finEn)}` : ' · en curso'}</strong>
                <span className={`etiqueta ${(i.finGuardado ?? i.guardado).clase}`}>{(i.finGuardado ?? i.guardado).texto}</span>
              </div>
              <div className="pequena suave">{i.operario}{i.resultado !== null ? ` · ${RESULTADOS[i.resultado] ?? i.resultado}` : ''}</div>
              {i.trabajoRealizado !== null && <div className="pequena">{i.trabajoRealizado}</div>}
            </li>
          ))}
        </ul>
        {vista.notas.length > 0 && (
          <ul style={{ ...LISTA, marginTop: 10 }} aria-label="Notas">
            {vista.notas.map((n) => (
              <li key={n.clave} className="tarjeta-linea">
                <div className="fila" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <strong>{TIPO_NOTA[n.tipo]} · {hora(n.ocurridoEn)}</strong>
                  <span className={`etiqueta ${n.guardado.clase}`}>{n.guardado.texto}</span>
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{n.texto}</div>
                {n.autor !== null && <div className="pequena suave">{n.autor}</div>}
              </li>
            ))}
          </ul>
        )}
        {vista.fotos.length > 0 && (
          <ul style={{ ...LISTA, marginTop: 10 }} aria-label="Fotos pendientes">
            {vista.fotos.map((f) => (
              <li key={f.clave} className="fila" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span>Foto ({f.contexto.toLowerCase()})</span>
                <span className={`etiqueta ${f.guardado.clase}`}>{f.guardado.texto}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

/** «Disponible descargado: 4 · Pendiente de sincronizar: −1 · Disponible estimado: 3» — siempre rotulado como estimación. */
function LineaEstimacion({ s }: { s: EstimacionStock }): ReactNode {
  const u = s.fila.unidad.codigo;
  return (
    <div className="pequena" aria-label={`Stock de ${s.fila.codigo}`}>
      <div>Disponible descargado: <strong>{cantidad(s.descargado)}</strong> {u}</div>
      {s.pendiente > 0 && (
        <>
          <div>Pendiente de sincronizar: <strong>−{cantidad(s.pendiente)}</strong> {u}</div>
          <div>Disponible estimado: <strong>{cantidad(s.estimado)}</strong> {u}</div>
        </>
      )}
    </div>
  );
}

function SeccionMaterial({ e }: { e: Entorno }): ReactNode {
  const { vista } = e;
  const porAlmacen = useMemo(() => {
    const m = new Map<string, EstimacionStock[]>();
    for (const s of e.stock) m.set(s.fila.idAlmacen, [...(m.get(s.fila.idAlmacen) ?? []), s]);
    return m;
  }, [e.stock]);
  return (
    <section className="panel" aria-label="Material">
      <div className="panel-cabecera"><h2>Material</h2></div>
      <div className="panel-cuerpo">
        <h3 className="titulo-grupo" style={{ marginTop: 0 }}>Previsto</h3>
        <p className="suave pequena" style={{ marginTop: 0 }}>Este parte no tiene material previsto. Lo previsto nunca cuenta como consumido.</p>

        <h3 className="titulo-grupo">Consumido</h3>
        {vista.consumos.length === 0 ? (
          <p className="suave pequena" style={{ marginTop: 0 }}>Todavía no se ha registrado material en este parte.</p>
        ) : (
          <ul style={LISTA} aria-label="Consumos">
            {vista.consumos.map((c) => (
              <li key={c.clave} className="tarjeta-linea">
                <div className="fila" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <strong>{c.articulo}</strong>
                  <span className={`etiqueta ${c.guardado.clase}`}>{c.guardado.estado === 'SINCRONIZADO' ? c.estado : c.guardado.texto}</span>
                </div>
                <div className="pequena">{c.cantidad} {c.unidad}{c.lote !== null ? ` · lote ${c.lote}` : ''}{c.almacen !== '' ? ` · ${c.almacen}` : ''}</div>
                {c.motivo !== null && <div className="nota error pequena" style={{ margin: '4px 0 0' }}>{c.motivo}</div>}
              </li>
            ))}
          </ul>
        )}

        <h3 className="titulo-grupo">Stock</h3>
        {e.fotografiadoEn !== null && <p className="pequena" style={{ marginTop: 0 }}><strong>{textoFotografiaStock(e.fotografiadoEn)}</strong>. Es una foto del momento: al sincronizar manda el stock real.</p>}
        {e.truncado && <p className="suave pequena">Sólo se muestran los primeros artículos con stock.</p>}
        {e.stock.length === 0 ? (
          <p className="suave pequena">No hay stock disponible en tu furgoneta ni en el almacén del centro.</p>
        ) : (
          [...porAlmacen.entries()].map(([idAlmacen, filas]) => {
            return (
              <div key={idAlmacen} style={{ marginBottom: 10 }}>
                <div className="suave pequena">{nombreAlmacen(e, idAlmacen) ?? 'Almacén'}</div>
                <ul style={LISTA}>
                  {filas.map((s) => (
                    <li key={`${s.fila.idAlmacen}:${s.fila.idArticulo}`} className="tarjeta-linea">
                      <strong>{s.fila.codigo} · {s.fila.descripcion}</strong>
                      <LineaEstimacion s={s} />
                      {s.fila.controlaSeries && <div className="suave pequena">Con número de serie: se consume con conexión.</div>}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function nombreAlmacen(e: Entorno, idAlmacen: string): string | null {
  const a = e.almacenes.find((x) => x.id === idAlmacen);
  if (a === undefined) return null;
  return a.propio ? `Tu furgoneta · ${a.descripcion}` : a.descripcion;
}

// ------------------------------------------------------------------ formularios

type Hacer = (f: () => Promise<ResultadoAccionMnt>, hecho: string) => Promise<boolean>;

function FormNota({ e, enviando, hacer }: { e: Entorno; enviando: boolean; hacer: Hacer }): ReactNode {
  const id = useId();
  const [tipo, setTipo] = useState<TipoNotaCampo>('DIAGNOSTICO');
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<string | null>(null);
  function enviar(): void {
    const prep = prepararNota(e.entorno, e.meta, tipo, texto);
    if (!prep.ok) { setError(prep.motivo); return; }
    setError(null);
    void hacer(() => ejecutarOperacionMnt(e.ctx, prep.op, prep.aviso), `${TIPO_NOTA[tipo]} añadido`);
  }
  return (
    <div className="tarjeta-linea-formulario panel" style={{ marginTop: 10 }}>
      <div className="panel-cuerpo">
        <div className="campo">
          <label htmlFor={`${id}-tipo`}>Tipo</label>
          <select id={`${id}-tipo`} value={tipo} onChange={(x) => setTipo(x.target.value as TipoNotaCampo)} style={TACTIL}>
            <option value="DIAGNOSTICO">Diagnóstico (qué has encontrado)</option>
            <option value="TRABAJO">Trabajo (qué has hecho)</option>
            <option value="OBSERVACION">Observación</option>
          </select>
        </div>
        <div className="campo">
          <label htmlFor={`${id}-texto`}>Nota</label>
          <textarea id={`${id}-texto`} rows={3} value={texto} onChange={(x) => { setTexto(x.target.value); setError(null); }} aria-invalid={error !== null} />
        </div>
        {error !== null && <p className="nota error" role="alert">{error}</p>}
        <p className="suave pequena">Cada nota queda en el historial con su hora: no sustituye a las anteriores.</p>
        <button type="button" className="principal" style={TACTIL} disabled={enviando} onClick={enviar}>{enviando ? 'Guardando…' : 'Guardar nota'}</button>
      </div>
    </div>
  );
}

function FormFoto({ e, enviando, hacer }: { e: Entorno; enviando: boolean; hacer: Hacer }): ReactNode {
  const id = useId();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [contexto, setContexto] = useState<'ANTES' | 'DURANTE' | 'DESPUES' | 'GENERAL'>('DURANTE');
  const [error, setError] = useState<string | null>(null);
  function enviar(): void {
    if (archivo === null) { setError('Elige una foto.'); return; }
    const prep = prepararFoto(e.entorno, e.meta);
    if (!prep.ok) { setError(prep.motivo); return; }
    const p = e.vista.campo.parte;
    void hacer(() => subirFotoParte(e.ctx, {
      archivo, contexto, idIntervencion: prep.idIntervencion, dependeDe: prep.dependeDe, idPt: p.id, idCentro: p.centro.id, titulo: p.titulo,
    }, prep.aviso), 'Foto añadida');
  }
  return (
    <div className="tarjeta-linea-formulario panel" style={{ marginTop: 10 }}>
      <div className="panel-cuerpo">
        <div className="campo">
          <label htmlFor={`${id}-foto`}>Foto</label>
          <input id={`${id}-foto`} type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
            onChange={(x) => { setArchivo(x.target.files?.[0] ?? null); setError(null); }} style={TACTIL} />
        </div>
        <div className="campo">
          <label htmlFor={`${id}-momento`}>¿Cuándo?</label>
          <select id={`${id}-momento`} value={contexto} onChange={(x) => setContexto(x.target.value as typeof contexto)} style={TACTIL}>
            <option value="ANTES">Antes de la reparación</option>
            <option value="DURANTE">Durante</option>
            <option value="DESPUES">Después</option>
            <option value="GENERAL">Otra</option>
          </select>
        </div>
        {error !== null && <p className="nota error" role="alert">{error}</p>}
        <button type="button" className="principal" style={TACTIL} disabled={enviando} onClick={enviar}>{enviando ? 'Guardando…' : 'Guardar foto'}</button>
      </div>
    </div>
  );
}

function FormConsumo({ e, enviando, hacer }: { e: Entorno; enviando: boolean; hacer: Hacer }): ReactNode {
  const id = useId();
  const elegibles = e.stock.filter((s) => !s.fila.controlaSeries);
  const [clave, setClave] = useState(elegibles[0] === undefined ? '' : `${elegibles[0].fila.idAlmacen}:${elegibles[0].fila.idArticulo}`);
  const [idLote, setIdLote] = useState('');
  const [cant, setCant] = useState('1');
  const [obs, setObs] = useState('');
  const [error, setError] = useState<string | null>(null);
  const elegida = elegibles.find((s) => `${s.fila.idAlmacen}:${s.fila.idArticulo}` === clave) ?? null;
  if (elegibles.length === 0) {
    return <p className="suave" style={{ marginTop: 10 }}>No hay material disponible en el stock descargado. Con conexión puedes registrarlo desde la ficha completa del parte.</p>;
  }
  function enviar(): void {
    if (elegida === null) { setError('Elige el material.'); return; }
    const prep = prepararConsumo(e.entorno, e.meta, { fila: elegida.fila, idLote: idLote === '' ? null : idLote, cantidad: cant, observaciones: obs, estimado: elegida.estimado });
    if (!prep.ok) { setError(prep.motivo); return; }
    setError(null);
    void hacer(() => ejecutarOperacionMnt(e.ctx, prep.op, prep.aviso), 'Material registrado');
  }
  return (
    <div className="tarjeta-linea-formulario panel" style={{ marginTop: 10 }}>
      <div className="panel-cuerpo">
        <div className="campo">
          <label htmlFor={`${id}-art`}>Material</label>
          <select id={`${id}-art`} value={clave} onChange={(x) => { setClave(x.target.value); setIdLote(''); setError(null); }} style={TACTIL}>
            {elegibles.map((s) => (
              <option key={`${s.fila.idAlmacen}:${s.fila.idArticulo}`} value={`${s.fila.idAlmacen}:${s.fila.idArticulo}`}>
                {s.fila.codigo} · {s.fila.descripcion} ({nombreAlmacen(e, s.fila.idAlmacen) ?? 'almacén'})
              </option>
            ))}
          </select>
        </div>
        {elegida !== null && <LineaEstimacion s={elegida} />}
        {elegida?.fila.controlaLotes === true && (
          <div className="campo">
            <label htmlFor={`${id}-lote`}>Lote</label>
            <select id={`${id}-lote`} value={idLote} onChange={(x) => setIdLote(x.target.value)} style={TACTIL}>
              <option value="">Elige el lote…</option>
              {elegida.fila.lotes.map((l) => <option key={l.id} value={l.id}>{l.codigo}{l.fechaCaducidad !== null ? ` · cad. ${l.fechaCaducidad}` : ''}</option>)}
            </select>
          </div>
        )}
        <div className="campo">
          <label htmlFor={`${id}-cant`}>Cantidad{elegida !== null ? ` (${elegida.fila.unidad.codigo})` : ''}</label>
          <input id={`${id}-cant`} inputMode="decimal" value={cant} onChange={(x) => { setCant(x.target.value); setError(null); }} style={TACTIL} />
        </div>
        <div className="campo">
          <label htmlFor={`${id}-obs`}>Observaciones (opcional)</label>
          <input id={`${id}-obs`} value={obs} onChange={(x) => setObs(x.target.value)} style={TACTIL} />
        </div>
        {error !== null && <p className="nota error" role="alert">{error}</p>}
        <p className="suave pequena">Sin conexión, el consumo queda pendiente: se descuenta del stock real cuando llega al servidor y lo valida tu responsable.</p>
        <button type="button" className="principal" style={TACTIL} disabled={enviando} onClick={enviar}>{enviando ? 'Guardando…' : 'Registrar consumo'}</button>
      </div>
    </div>
  );
}

const TEXTO_DECISION: Readonly<Record<PayloadMntFinalizar['decision'], string>> = {
  CONTINUAR: 'Seguir con el parte (otra visita más tarde)',
  PENDIENTE: 'Dejar el parte pendiente',
  FINALIZAR: 'Finalizar el parte (se cierra después, con conexión)',
};

function FormFin({ e, enviando, hacer }: { e: Entorno; enviando: boolean; hacer: Hacer }): ReactNode {
  const id = useId();
  const [trabajo, setTrabajo] = useState('');
  const [resultado, setResultado] = useState<PayloadMntFinalizar['resultado'] | ''>('');
  const [decision, setDecision] = useState<PayloadMntFinalizar['decision']>('CONTINUAR');
  const [motivo, setMotivo] = useState('');
  const [detalle, setDetalle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const opciones = opcionesFin(e.vista, resultado);
  useEffect(() => { if (!opciones.includes(decision)) setDecision('CONTINUAR'); }, [opciones, decision]);
  function enviar(): void {
    const prep = prepararFin(e.entorno, e.meta, { trabajoRealizado: trabajo, resultado, decision, motivoPendiente: motivo === '' ? null : motivo, detallePendiente: detalle });
    if (!prep.ok) { setError(prep.motivo); return; }
    setError(null);
    void hacer(() => ejecutarOperacionMnt(e.ctx, prep.op, prep.aviso), 'Intervención finalizada');
  }
  return (
    <div className="tarjeta-linea-formulario panel" style={{ marginTop: 10 }}>
      <div className="panel-cuerpo">
        <div className="campo">
          <label htmlFor={`${id}-trabajo`}>Trabajo realizado</label>
          <textarea id={`${id}-trabajo`} rows={3} value={trabajo} onChange={(x) => { setTrabajo(x.target.value); setError(null); }} />
        </div>
        <fieldset className="campo">
          <legend>Resultado</legend>
          {Object.entries(RESULTADOS).map(([k, t]) => (
            <label key={k} className="fila opcion-grande" style={TACTIL}>
              <input type="radio" name={`${id}-resultado`} value={k} checked={resultado === k} onChange={() => setResultado(k as PayloadMntFinalizar['resultado'])} /> {t}
            </label>
          ))}
        </fieldset>
        <fieldset className="campo">
          <legend>Siguiente paso</legend>
          {opciones.map((k) => (
            <label key={k} className="fila opcion-grande" style={TACTIL}>
              <input type="radio" name={`${id}-decision`} value={k} checked={decision === k} onChange={() => setDecision(k)} /> {TEXTO_DECISION[k]}
            </label>
          ))}
          {e.vista.otrasAbiertas > 0 && <p className="suave pequena">Otro técnico sigue trabajando en este parte: el parte cambiará de estado cuando terminen todos.</p>}
        </fieldset>
        {decision === 'PENDIENTE' && (
          <>
            <div className="campo">
              <label htmlFor={`${id}-motivo`}>Motivo</label>
              <select id={`${id}-motivo`} value={motivo} onChange={(x) => setMotivo(x.target.value)} style={TACTIL}>
                <option value="">Elige el motivo…</option>
                {Object.entries(MOTIVOS_PENDIENTE).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
              </select>
            </div>
            <div className="campo">
              <label htmlFor={`${id}-detalle`}>Detalle{motivo === 'OTRO' ? '' : ' (opcional)'}</label>
              <input id={`${id}-detalle`} value={detalle} onChange={(x) => setDetalle(x.target.value)} style={TACTIL} />
            </div>
          </>
        )}
        {error !== null && <p className="nota error" role="alert">{error}</p>}
        <button type="button" className="principal grande" style={TACTIL} disabled={enviando} onClick={enviar}>{enviando ? 'Guardando…' : 'Finalizar intervención'}</button>
      </div>
    </div>
  );
}
